import { useState, useRef, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';
import { Z_INDEX } from '../../styles/zIndex';

type Props = {
  /** 文字列だけでなく、見出し + 本文のような複合ノードも渡せる */
  content: ReactNode;
  children: ReactNode;
  /** ツールチップの表示位置。デフォルトは 'top'（上側）。'bottom' で下側に表示 */
  placement?: 'top' | 'bottom';
  /** ラッパー div に付与するクラス名 */
  className?: string;
  /**
   * 配色テーマ。
   * - 'default': エンドユーザー画面用 (ダーク bg + ゴールド系テキスト)
   * - 'org-admin': org-admin/admin 画面用 (indigo bg + 白テキスト)
   */
  variant?: 'default' | 'org-admin';
  /**
   * true の間はツールチップを出さない (表示中なら閉じる)。
   * トリガーから開いたメニュー・ポップオーバーとツールチップが重なって読めなくなるのを防ぐ
   * (ツールチップの z-index はドロップダウンより上なので、開いている間は抑止する側で解決する)。
   */
  disabled?: boolean;
  /** ホバーしてから表示するまでの待ち時間 (ミリ秒)。省略時は即時。離す・押すと待ちを取り消す */
  delayMs?: number;
};

export function Tooltip({
  content,
  children,
  placement = 'top',
  className,
  variant = 'default',
  disabled = false,
  delayMs = 0,
}: Props) {
  const [isVisible, setIsVisible] = useState(false);
  const showTimerRef = useRef<number | null>(null);
  const clearShowTimer = () => {
    if (showTimerRef.current != null) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  };
  useEffect(() => clearShowTimer, []);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [arrowPosition, setArrowPosition] = useState<'top' | 'bottom'>('top');
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let top: number;
    let left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
    let arrow: 'top' | 'bottom';

    if (placement === 'bottom') {
      // 下に配置
      top = triggerRect.bottom + 10;
      arrow = 'bottom'; // 矢印は上向き

      // 画面外に出る場合は上に調整
      if (top + tooltipRect.height > window.innerHeight) {
        top = triggerRect.top - tooltipRect.height - 10;
        arrow = 'top';
      }
    } else {
      // 上に配置（デフォルト）
      top = triggerRect.top - tooltipRect.height - 10;
      arrow = 'top'; // 矢印は下向き

      // 画面外に出る場合は下に調整
      if (top < 0) {
        top = triggerRect.bottom + 10;
        arrow = 'bottom';
      }
    }

    // 左右の画面外調整
    if (left < 0) {
      left = 8;
    }
    if (left + tooltipRect.width > window.innerWidth) {
      left = window.innerWidth - tooltipRect.width - 8;
    }

    setPosition({ top, left });
    setArrowPosition(arrow);
  };

  useEffect(() => {
    if (isVisible) {
      updatePosition();
    }
  }, [isVisible]);

  // メニューを開いた等で抑止に切り替わったら、ホバーしたままでも閉じる
  useEffect(() => {
    if (disabled) {
      clearShowTimer();
      setIsVisible(false);
    }
  }, [disabled]);

  const handleMouseEnter = () => {
    if (disabled) return;
    if (delayMs > 0) {
      clearShowTimer();
      showTimerRef.current = window.setTimeout(() => {
        showTimerRef.current = null;
        setIsVisible(true);
      }, delayMs);
      return;
    }
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    clearShowTimer();
    setIsVisible(false);
  };

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        // クリック (押下) した時点でツールチップを閉じる。ボタンを押してメニューやピッカーを
        // 開いたあとも、マウスが離れるまでツールチップが残って邪魔になるのを防ぐ。
        onMouseDown={handleMouseLeave}
        // キーボード操作やプログラムからの click (mousedown を伴わない) でも閉じる
        onClick={handleMouseLeave}
        className={className}
        style={className ? undefined : { display: 'inline-block' }}
      >
        {children}
      </div>
      {isVisible && !disabled &&
        createPortal(
          <div
            ref={tooltipRef}
            className={`${styles.tooltip} ${variant === 'org-admin' ? styles.tooltipOrgAdmin : ''} ${arrowPosition === 'top' ? styles.arrowBottom : styles.arrowTop}`}
            style={{
              position: 'fixed',
              top: `${position.top}px`,
              left: `${position.left}px`,
              zIndex: Z_INDEX.tooltip,
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
}
